import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// Full-page drag & drop upload for Page Photos.
//
// Expects data-values:
// - uploadUrlValue: POST endpoint that accepts page_photo[images][] (signed blob ids or files)
// - directUploadUrlValue: ActiveStorage direct upload endpoint
// - turboFrameIdValue: turbo-frame id to target for server-rendered updates
//
// Expects targets:
// - overlay, status, progress, progressList
export default class extends Controller {
  static values = {
    uploadUrl: String,
    turboFrameId: String,
    directUploadUrl: String,
    maxBytes: Number,
  };

  static targets = ["overlay", "status", "progress", "progressList"];

  connect() {
    this.dragDepth = 0;
    this.activeUploads = 0;

    this.onDragEnter = this.onDragEnter.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    document.addEventListener("dragenter", this.onDragEnter);
    document.addEventListener("dragover", this.onDragOver);
    document.addEventListener("dragleave", this.onDragLeave);
    document.addEventListener("drop", this.onDrop);
    document.addEventListener("keydown", this.onKeyDown);
  }

  disconnect() {
    document.removeEventListener("dragenter", this.onDragEnter);
    document.removeEventListener("dragover", this.onDragOver);
    document.removeEventListener("dragleave", this.onDragLeave);
    document.removeEventListener("drop", this.onDrop);
    document.removeEventListener("keydown", this.onKeyDown);
  }

  onKeyDown(event) {
    if (event.key === "Escape") this.hideOverlay();
  }

  onDragEnter(event) {
    if (!this.hasFiles(event)) return;
    this.dragDepth += 1;
    this.showOverlay();
    this.setStatus("Drop photos to upload", { kind: "info" });
  }

  onDragOver(event) {
    if (!this.hasFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    this.showOverlay();
  }

  onDragLeave(event) {
    if (!this.hasFiles(event)) return;
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0 && this.activeUploads === 0) this.hideOverlay();
  }

  async onDrop(event) {
    if (!this.hasFiles(event)) return;
    event.preventDefault();

    this.dragDepth = 0;
    const files = Array.from(event.dataTransfer?.files || []).filter(Boolean);
    if (files.length === 0) return;

    const { validFiles, invalidFiles, tooLargeFiles } = this.partitionFiles(files);

    if (invalidFiles.length > 0 || tooLargeFiles.length > 0) {
      const parts = [];
      if (invalidFiles.length > 0) parts.push(this.invalidTypeMessage(invalidFiles));
      if (tooLargeFiles.length > 0) parts.push(this.tooLargeMessage(tooLargeFiles));
      this.setStatus(parts.join(" "), { kind: "error" });

      // If *everything* is invalid, don’t keep the overlay stuck open.
      if (validFiles.length === 0) {
        window.setTimeout(() => this.hideOverlay(), 1600);
        return;
      }
    } else {
      this.setStatus("Uploading…", { kind: "info" });
    }

    this.showProgress();
    this.clearProgressList();

    try {
      const signedIds = await this.directUploadFiles(validFiles);
      if (signedIds.length === 0) {
        this.setStatus("Upload failed.", { kind: "error" });
        window.setTimeout(() => this.hideOverlay(), 2000);
        return;
      }

      this.setStatus("Saving…", { kind: "info" });
      await this.submitToTurboFrame(signedIds);
      this.setStatus("Done", { kind: "success" });
      window.setTimeout(() => this.hideOverlay(), 700);
    } catch (e) {
      this.setStatus(e?.message || "Upload failed.", { kind: "error" });
      window.setTimeout(() => this.hideOverlay(), 2000);
    } finally {
      this.activeUploads = 0;
    }
  }

  // ---- Validation helpers

  allowedContentTypes() {
    return new Set(["image/png", "image/jpeg", "image/webp"]);
  }

  maxBytes() {
    // Provided by the server (see pages/_photo_drop_anywhere.html.erb).
    return Number(this.maxBytesValue || 0);
  }

  partitionFiles(files) {
    const allowed = this.allowedContentTypes();
    const validFiles = [];
    const invalidFiles = [];
    const tooLargeFiles = [];
    const maxBytes = this.maxBytes();

    for (const file of files) {
      if (!file) continue;
      if (maxBytes > 0 && Number(file.size || 0) > maxBytes) {
        tooLargeFiles.push(file);
        continue;
      }

      const type = (file.type || "").toLowerCase();
      if (type && allowed.has(type)) {
        validFiles.push(file);
        continue;
      }

      // Some browsers/OSes can provide an empty `type` for dragged files.
      // Fall back to extension sniffing.
      const name = (file.name || "").toLowerCase();
      const extOk = name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".webp");
      if (extOk) validFiles.push(file);
      else invalidFiles.push(file);
    }

    return { validFiles, invalidFiles, tooLargeFiles };
  }

  invalidTypeMessage(files) {
    if (files.length === 1) return `“${files[0].name}” isn’t a supported image type. PNG, JPEG, or WebP only.`;
    return `${files.length} files aren’t supported image types. PNG, JPEG, or WebP only.`;
  }

  tooLargeMessage(files) {
    const maxMb = Math.round(this.maxBytes() / 1024 / 1024);
    if (files.length === 1) return `“${files[0].name}” is too large (max ${maxMb} MB).`;
    return `${files.length} files are too large (max ${maxMb} MB).`;
  }

  // ---- Direct upload

  async directUploadFiles(files) {
    const signedIds = [];

    for (const file of files) {
      const item = this.buildProgressItem(file);
      this.progressListTarget?.appendChild(item.root);

      this.activeUploads += 1;

      const signedId = await new Promise((resolve, reject) => {
        const upload = new DirectUpload(file, this.directUploadUrlValue);

        // Hook progress on the actual file upload XHR.
        upload.directUploadWillStoreFileWithXHR = (xhr) => {
          xhr.upload.addEventListener("progress", (event) => {
            if (!event.lengthComputable) return;
            const pct = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)));
            item.setProgress(pct);
          });
        };

        upload.create((error, blob) => {
          if (error) return reject(new Error(error.toString ? error.toString() : "Upload failed"));
          if (!blob?.signed_id) return reject(new Error("Upload failed"));
          resolve(blob.signed_id);
        });
      }).catch((err) => {
        item.setError(err?.message || "Upload failed");
        return null;
      });

      if (signedId) {
        item.setDone();
        signedIds.push(signedId);
      }

      this.activeUploads = Math.max(0, this.activeUploads - 1);
    }

    return signedIds;
  }

  // ---- Turbo submit (no nested forms)

  submitToTurboFrame(signedIds) {
    return new Promise((resolve) => {
      // Create a temporary form outside the page edit <form> to avoid nesting.
      const form = document.createElement("form");
      form.method = "post";
      form.action = this.uploadUrlValue;
      form.enctype = "multipart/form-data";
      form.style.display = "none";
      if (this.turboFrameIdValue) form.setAttribute("data-turbo-frame", this.turboFrameIdValue);

      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
      if (csrf) {
        const token = document.createElement("input");
        token.type = "hidden";
        token.name = "authenticity_token";
        token.value = csrf;
        form.appendChild(token);
      }

      for (const signedId of signedIds) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "page_photo[images][]";
        input.value = signedId;
        form.appendChild(input);
      }

      // Tell the server not to show a success notice for drag/drop uploads.
      const suppress = document.createElement("input");
      suppress.type = "hidden";
      suppress.name = "page_photo[suppress_notice]";
      suppress.value = "1";
      form.appendChild(suppress);

      // Resolve when Turbo finishes loading the target frame.
      const onLoad = (event) => {
        const frame = event.target;
        if (!(frame instanceof Element)) return;
        if (frame.id !== this.turboFrameIdValue) return;
        document.removeEventListener("turbo:frame-load", onLoad);
        cleanup();
        resolve();
      };

      const cleanup = () => {
        try {
          document.removeEventListener("turbo:frame-load", onLoad);
        } catch {
          // ignore
        }
        form.remove();
      };

      if (this.turboFrameIdValue) document.addEventListener("turbo:frame-load", onLoad);

      document.body.appendChild(form);
      form.requestSubmit();

      // Fallback: don’t hang forever if Turbo doesn’t fire (e.g., network error).
      window.setTimeout(() => {
        cleanup();
        resolve();
      }, 10000);
    });
  }

  // ---- Overlay UI

  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.hidden = false;
      this.overlayTarget.setAttribute("aria-hidden", "false");
      this.overlayTarget.classList.add("is-active");
    }
  }

  hideOverlay() {
    if (this.activeUploads > 0) return;
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("is-active");
      this.overlayTarget.hidden = true;
      this.overlayTarget.setAttribute("aria-hidden", "true");
    }
    this.hideProgress();
    this.clearProgressList();
    this.setStatus("Drop photos to upload", { kind: "info", quiet: true });
  }

  setStatus(text, { kind = "info", quiet = false } = {}) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = text;
    this.statusTarget.classList.remove("text-danger", "text-success", "text-body-secondary");
    if (quiet) {
      this.statusTarget.classList.add("text-body-secondary");
      return;
    }
    if (kind === "error") this.statusTarget.classList.add("text-danger");
    if (kind === "success") this.statusTarget.classList.add("text-success");
  }

  showProgress() {
    if (this.hasProgressTarget) this.progressTarget.hidden = false;
  }

  hideProgress() {
    if (this.hasProgressTarget) this.progressTarget.hidden = true;
  }

  clearProgressList() {
    if (this.hasProgressListTarget) this.progressListTarget.innerHTML = "";
  }

  buildProgressItem(file) {
    const root = document.createElement("div");
    root.className = "mt-2";

    const header = document.createElement("div");
    header.className = "d-flex align-items-center justify-content-between gap-2 small";

    const name = document.createElement("div");
    name.className = "text-truncate";
    name.textContent = file?.name || "Upload";

    const status = document.createElement("div");
    status.className = "text-body-secondary";
    status.textContent = "Queued";

    header.appendChild(name);
    header.appendChild(status);

    const progress = document.createElement("div");
    progress.className = "progress mt-1";
    progress.style.height = "6px";

    const bar = document.createElement("div");
    bar.className = "progress-bar progress-bar-striped progress-bar-animated";
    bar.setAttribute("role", "progressbar");
    bar.setAttribute("aria-valuemin", "0");
    bar.setAttribute("aria-valuemax", "100");
    bar.setAttribute("aria-valuenow", "0");
    bar.style.width = "0%";

    progress.appendChild(bar);
    root.appendChild(header);
    root.appendChild(progress);

    const setProgress = (pct) => {
      bar.style.width = `${pct}%`;
      bar.setAttribute("aria-valuenow", `${pct}`);
      status.textContent = `${pct}%`;
    };

    const setError = (msg) => {
      root.classList.add("text-danger");
      bar.classList.remove("progress-bar-animated");
      bar.classList.remove("progress-bar-striped");
      bar.classList.add("bg-danger");
      status.classList.remove("text-body-secondary");
      status.classList.add("text-danger");
      status.textContent = msg || "Upload failed";
    };

    const setDone = () => {
      bar.classList.remove("progress-bar-animated");
      bar.classList.remove("progress-bar-striped");
      bar.classList.add("bg-success");
      bar.style.width = "100%";
      bar.setAttribute("aria-valuenow", "100");
      status.textContent = "Done";
    };

    return { root, setProgress, setError, setDone };
  }

  hasFiles(event) {
    const types = event.dataTransfer?.types;
    if (!types) return false;
    // Older Safari doesn’t support `includes` on DOMStringList.
    for (const t of types) {
      if (t === "Files") return true;
    }
    return false;
  }
}
